<script setup lang="ts">
/**
 * Presentational list of reverse map landmark links (#172).
 * Data comes from `useEntityMapRefs` in the Relationships section host.
 */
import { displayFileName } from "../lib/fileNames";
import type { MapLandmarkRef } from "../lib/mapLandmarkLinks";

const props = withDefaults(
  defineProps<{
    /** Landmark → map reverse links for the active entry. */
    items?: readonly MapLandmarkRef[];
  }>(),
  { items: () => [] },
);

const emit = defineEmits<{
  openMap: [row: MapLandmarkRef];
}>();

function targetLabel(row: MapLandmarkRef): string {
  return displayFileName(row.mapFile);
}
</script>

<template>
  <ul v-if="props.items.length > 0" class="entity-map-refs-list">
    <li
      v-for="row in props.items"
      :key="`${row.mapFile}:${row.landmarkId}`"
      class="entity-map-ref-row"
    >
      <button
        type="button"
        class="entity-map-ref-main"
        :title="`Open ${displayFileName(row.mapFile)}`"
        @click="emit('openMap', row)"
      >
        <span class="entity-map-ref-phrase">
          <span class="entity-map-ref-relation">on map</span>
          <span class="entity-map-ref-target">{{ targetLabel(row) }}</span>
        </span>
        <span v-if="row.caption.length > 0" class="entity-map-ref-note">{{
          row.caption
        }}</span>
      </button>
    </li>
  </ul>
</template>

<style scoped>
.entity-map-refs-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.entity-map-ref-row {
  border-radius: 0.25rem;
}

.entity-map-ref-row:hover {
  background: var(--hover-fill);
}

.entity-map-ref-main {
  appearance: none;
  border: 0;
  background: transparent;
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.08rem;
  padding: 0.35rem 0.45rem;
  text-align: left;
  font: inherit;
  cursor: pointer;
}

.entity-map-ref-phrase {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.35rem;
  min-width: 0;
}

.entity-map-ref-target {
  font-size: 0.88rem;
  color: var(--text);
}

.entity-map-ref-relation {
  font-size: 0.78rem;
  color: var(--text-muted);
}

.entity-map-ref-note {
  font-size: 0.78rem;
  line-height: 1.35;
  color: color-mix(in oklch, var(--text-muted) 90%, transparent);
}
</style>
